"use client";
import React from "react";
import type { TagFilter } from "@/types/tag-filters";

/**
 * Full-width promotional banner for a `type: "banner"` tag filter (e.g. a
 * closeout sale). Functionally it is the same toggle as a tag button — one
 * press adds the tag key to ?tags=, another removes it — but because a banner
 * doesn't read as a toggle, it carries its own state cues:
 *
 *  - Inactive: flat copper strip with the tag's `label` centered.
 *  - Active: a slimmer, darker strip with a check mark, the tag's succinct
 *    `activeLabel` (falls back to `label`), and a quiet "Back to full
 *    inventory" hint so visitors can see they're looking at a subset and how
 *    to get back out.
 *
 * The whole strip is a single <button aria-pressed> so the hit target is
 * generous on touch and the state is exposed to assistive tech.
 */
const TagBanner: React.FC<{
  tag: TagFilter;
  isActive: boolean;
  onToggle: () => void;
}> = ({ tag, isActive, onToggle }) => {
  const label = isActive ? tag.activeLabel ?? tag.label : tag.label;

  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onToggle}
      className={`group block w-full text-white hover:cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${
        isActive ? "bg-gdeepercopper hover:bg-gdeepcopper" : "bg-gcopper hover:bg-gdeepcopper"
      }`}
    >
      <span
        className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 text-center ${
          isActive ? "py-2" : "py-3"
        }`}
      >
        {/* Check + label share one inline box so they wrap together on narrow
            screens instead of the check mark landing on its own line. */}
        <span className="inline-flex items-center gap-2">
          {isActive && (
            <svg
              className="h-4 w-4 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}

          {/* Label is admin-authored HTML from Cosmos (bold spans, emoji, etc.).
              Trusted content only — see the note on TagFilter.label. */}
          <span
            className={`font-roboto-condensed font-bold tracking-wide leading-tight ${
              isActive ? "text-base lg:text-lg" : "text-lg lg:text-xl"
            }`}
            dangerouslySetInnerHTML={{ __html: label }}
          />
        </span>

        {/* Quiet exit hint — the banner is a toggle, but nothing about a banner
            says so, so name the way out. Plain text, no pill. */}
        {isActive && (
          <span className="inline-flex items-center gap-1 text-sm text-white/80 underline-offset-2 group-hover:text-white group-hover:underline">
            <span aria-hidden="true" className="hidden sm:inline text-white/50">|</span>
            Back to full inventory
          </span>
        )}
      </span>
    </button>
  );
};

export default TagBanner;
